/**
 * i18next Configuration
 *
 * Configuracao do react-i18next para internacionalizacao.
 * Usa recursos bundled (importados diretamente) para garantir funcionamento.
 *
 * @since 2025-12-16
 * @updated 2025-12-16 - Mudado para recursos bundled
 * @epic INIT-0045-03
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// ==========================================
// IMPORTAR RECURSOS DE TRADUCAO (BUNDLED)
// ==========================================

// pt-BR
import ptBRCommon from '@/locales/pt-BR/common.json';
import ptBRButtons from '@/locales/pt-BR/buttons.json';
import ptBRErrors from '@/locales/pt-BR/errors.json';
import ptBRValidation from '@/locales/pt-BR/validation.json';
import ptBRNavigation from '@/locales/pt-BR/modules/navigation.json';
import ptBRPerson from '@/locales/pt-BR/modules/person.json';
import ptBRResource from '@/locales/pt-BR/modules/resource.json';
import ptBRSettings from '@/locales/pt-BR/modules/settings.json';
import ptBRAccess from '@/locales/pt-BR/modules/access.json';
import ptBRBudget from '@/locales/pt-BR/modules/budget.json';
import ptBRStore from '@/locales/pt-BR/modules/store.json';
import ptBRCurrency from '@/locales/pt-BR/modules/currency.json';
import ptBRFragment from '@/locales/pt-BR/modules/fragment.json';
import ptBRImport from '@/locales/pt-BR/modules/import.json';
import ptBRRanking from '@/locales/pt-BR/modules/ranking.json';
import ptBRCampaign from '@/locales/pt-BR/modules/campaign.json';
import ptBRReports from '@/locales/pt-BR/modules/reports.json';
import ptBRDates from '@/locales/pt-BR/dates.json';
import ptBRProfile from '@/locales/pt-BR/profile.json';
import ptBRContent from '@/locales/pt-BR/content.json';
import ptBRIntegration from '@/locales/pt-BR/modules/integration.json';
import ptBRQuiz from '@/locales/pt-BR/modules/quiz.json';
import ptBRProduct from '@/locales/pt-BR/modules/product.json';
import ptBRFunction from '@/locales/pt-BR/modules/function.json';
import ptBRFinance from '@/locales/pt-BR/modules/finance.json';
import ptBRSocialPosts from '@/locales/pt-BR/modules/social-posts.json';
import ptBRCache from '@/locales/pt-BR/modules/cache.json';
import ptBRSac from '@/locales/pt-BR/modules/sac.json';

// en-US
import enUSCommon from '@/locales/en-US/common.json';
import enUSButtons from '@/locales/en-US/buttons.json';
import enUSErrors from '@/locales/en-US/errors.json';
import enUSValidation from '@/locales/en-US/validation.json';
import enUSNavigation from '@/locales/en-US/modules/navigation.json';
import enUSPerson from '@/locales/en-US/modules/person.json';
import enUSResource from '@/locales/en-US/modules/resource.json';
import enUSSettings from '@/locales/en-US/modules/settings.json';
import enUSAccess from '@/locales/en-US/modules/access.json';
import enUSStore from '@/locales/en-US/modules/store.json';
import enUSCurrency from '@/locales/en-US/modules/currency.json';
import enUSFragment from '@/locales/en-US/modules/fragment.json';
import enUSImport from '@/locales/en-US/modules/import.json';
import enUSDates from '@/locales/en-US/dates.json';
import enUSProfile from '@/locales/en-US/profile.json';
import enUSContent from '@/locales/en-US/content.json';
import enUSIntegration from '@/locales/en-US/modules/integration.json';
import enUSProduct from '@/locales/en-US/modules/product.json';
import enUSFunction from '@/locales/en-US/modules/function.json';
import enUSSocialPosts from '@/locales/en-US/modules/social-posts.json';
import enUSSac from '@/locales/en-US/modules/sac.json';

// es-ES
import esESCommon from '@/locales/es-ES/common.json';
import esESButtons from '@/locales/es-ES/buttons.json';
import esESErrors from '@/locales/es-ES/errors.json';
import esESValidation from '@/locales/es-ES/validation.json';
import esESNavigation from '@/locales/es-ES/modules/navigation.json';
import esESPerson from '@/locales/es-ES/modules/person.json';
import esESResource from '@/locales/es-ES/modules/resource.json';
import esESSettings from '@/locales/es-ES/modules/settings.json';
import esESAccess from '@/locales/es-ES/modules/access.json';
import esESCurrency from '@/locales/es-ES/modules/currency.json';
import esESFragment from '@/locales/es-ES/modules/fragment.json';
import esESImport from '@/locales/es-ES/modules/import.json';
import esESDates from '@/locales/es-ES/dates.json';
import esESProfile from '@/locales/es-ES/profile.json';
import esESContent from '@/locales/es-ES/content.json';
import esESProduct from '@/locales/es-ES/modules/product.json';
import esESFunction from '@/locales/es-ES/modules/function.json';
import esESStore from '@/locales/es-ES/modules/store.json';
import esESSac from '@/locales/es-ES/modules/sac.json';

// fr-FR
import frFRCommon from '@/locales/fr-FR/common.json';
import frFRButtons from '@/locales/fr-FR/buttons.json';
import frFRErrors from '@/locales/fr-FR/errors.json';
import frFRValidation from '@/locales/fr-FR/validation.json';
import frFRNavigation from '@/locales/fr-FR/modules/navigation.json';
import frFRPerson from '@/locales/fr-FR/modules/person.json';
import frFRResource from '@/locales/fr-FR/modules/resource.json';
import frFRSettings from '@/locales/fr-FR/modules/settings.json';
import frFRFragment from '@/locales/fr-FR/modules/fragment.json';
import frFRImport from '@/locales/fr-FR/modules/import.json';
import frFRDates from '@/locales/fr-FR/dates.json';
import frFRProduct from '@/locales/fr-FR/modules/product.json';
import frFRFunction from '@/locales/fr-FR/modules/function.json';

// ja-JP
import jaJPCommon from '@/locales/ja-JP/common.json';
import jaJPButtons from '@/locales/ja-JP/buttons.json';
import jaJPErrors from '@/locales/ja-JP/errors.json';
import jaJPValidation from '@/locales/ja-JP/validation.json';
import jaJPNavigation from '@/locales/ja-JP/modules/navigation.json';
import jaJPPerson from '@/locales/ja-JP/modules/person.json';
import jaJPResource from '@/locales/ja-JP/modules/resource.json';
import jaJPSettings from '@/locales/ja-JP/modules/settings.json';
import jaJPFragment from '@/locales/ja-JP/modules/fragment.json';
import jaJPDates from '@/locales/ja-JP/dates.json';

// zh-CN
import zhCNCommon from '@/locales/zh-CN/common.json';
import zhCNButtons from '@/locales/zh-CN/buttons.json';
import zhCNErrors from '@/locales/zh-CN/errors.json';
import zhCNValidation from '@/locales/zh-CN/validation.json';
import zhCNNavigation from '@/locales/zh-CN/modules/navigation.json';
import zhCNPerson from '@/locales/zh-CN/modules/person.json';
import zhCNResource from '@/locales/zh-CN/modules/resource.json';
import zhCNSettings from '@/locales/zh-CN/modules/settings.json';
import zhCNFragment from '@/locales/zh-CN/modules/fragment.json';
import zhCNDates from '@/locales/zh-CN/dates.json';

// ar-SA (Arabic)
import arSACommon from '@/locales/ar-SA/common.json';
import arSAButtons from '@/locales/ar-SA/buttons.json';
import arSAErrors from '@/locales/ar-SA/errors.json';
import arSAValidation from '@/locales/ar-SA/validation.json';
import arSANavigation from '@/locales/ar-SA/modules/navigation.json';
import arSAPerson from '@/locales/ar-SA/modules/person.json';
import arSAResource from '@/locales/ar-SA/modules/resource.json';
import arSASettings from '@/locales/ar-SA/modules/settings.json';
import arSAFragment from '@/locales/ar-SA/modules/fragment.json';
import arSADates from '@/locales/ar-SA/dates.json';

// de-DE (German)
import deDECommon from '@/locales/de-DE/common.json';
import deDEErrors from '@/locales/de-DE/errors.json';
import deDEValidation from '@/locales/de-DE/validation.json';
import deDEResource from '@/locales/de-DE/modules/resource.json';
import deDESettings from '@/locales/de-DE/modules/settings.json';
import deDEStore from '@/locales/de-DE/modules/store.json';
import deDEDates from '@/locales/de-DE/dates.json';

// ==========================================
// IDIOMAS SUPORTADOS
// ==========================================

export const supportedLanguages = [
  { code: 'pt-BR', name: 'Português', flag: '\u{1F1E7}\u{1F1F7}' },
  { code: 'en-US', name: 'English', flag: '\u{1F1FA}\u{1F1F8}' },
  { code: 'es-ES', name: 'Español', flag: '\u{1F1EA}\u{1F1F8}' },
  { code: 'fr-FR', name: 'Français', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: 'de-DE', name: 'Deutsch', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: 'ja-JP', name: '日本語', flag: '\u{1F1EF}\u{1F1F5}' },
  { code: 'zh-CN', name: '中文', flag: '\u{1F1E8}\u{1F1F3}' },
  { code: 'ar-SA', name: 'العربية', flag: '\u{1F1F8}\u{1F1E6}' },
] as const;

export type SupportedLanguageCode = typeof supportedLanguages[number]['code'];

// ==========================================
// NAMESPACES
// ==========================================

export const defaultNamespaces = ['common', 'buttons', 'errors', 'validation', 'dates'];
export const moduleNamespaces = ['modules/navigation', 'modules/person', 'modules/access', 'modules/budget', 'modules/settings', 'settings', 'modules/resource', 'modules/import', 'modules/ranking', 'modules/campaign', 'modules/reports', 'modules/quiz', 'modules/product', 'modules/function', 'modules/store', 'modules/currency', 'modules/cache', 'modules/sac', 'resource', 'store', 'currency', 'ranking', 'campaign', 'reports', 'quiz', 'product', 'function', 'cache', 'sac', 'modules/fragment'];

// ==========================================
// RECURSOS BUNDLED
// ==========================================

const resources = {
  'pt-BR': {
    common: ptBRCommon,
    buttons: ptBRButtons,
    errors: ptBRErrors,
    validation: ptBRValidation,
    'modules/navigation': ptBRNavigation,
    'modules/person': ptBRPerson,
    'modules/access': ptBRAccess,
    'modules/budget': ptBRBudget,
    'modules/finance': ptBRFinance,
    'modules/settings': ptBRSettings,
    settings: ptBRSettings, // Alias para uso em useTranslation('settings')
    'modules/resource': ptBRResource,
    'modules/import': ptBRImport,
    resource: ptBRResource, // Alias para uso direto em useTranslation('resource')
    store: ptBRStore,
    'modules/store': ptBRStore,
    currency: ptBRCurrency,
    'modules/currency': ptBRCurrency,
    'modules/fragment': ptBRFragment,
    'modules/ranking': ptBRRanking,
    ranking: ptBRRanking,
    'modules/campaign': ptBRCampaign,
    campaign: ptBRCampaign,
    'modules/reports': ptBRReports,
    reports: ptBRReports,
    dates: ptBRDates,
    profile: ptBRProfile,
    content: ptBRContent,
    integration: ptBRIntegration,
    'modules/integration': ptBRIntegration,
    'modules/quiz': ptBRQuiz,
    quiz: ptBRQuiz,
    'modules/product': ptBRProduct,
    product: ptBRProduct,
    'modules/function': ptBRFunction,
    function: ptBRFunction,
    'modules/cache': ptBRCache,
    cache: ptBRCache,
    'modules/social-posts': ptBRSocialPosts,
    'social-posts': ptBRSocialPosts,
    'modules/sac': ptBRSac,
    sac: ptBRSac,
  },
  // Alias pt → pt-BR
  'pt': {
    common: ptBRCommon,
    buttons: ptBRButtons,
    errors: ptBRErrors,
    validation: ptBRValidation,
    'modules/navigation': ptBRNavigation,
    'modules/person': ptBRPerson,
    'modules/access': ptBRAccess,
    'modules/budget': ptBRBudget,
    'modules/finance': ptBRFinance,
    'modules/settings': ptBRSettings,
    settings: ptBRSettings, // Alias para uso em useTranslation('settings')
    'modules/resource': ptBRResource,
    'modules/import': ptBRImport,
    resource: ptBRResource,
    store: ptBRStore,
    'modules/store': ptBRStore,
    currency: ptBRCurrency,
    'modules/currency': ptBRCurrency,
    'modules/fragment': ptBRFragment,
    'modules/ranking': ptBRRanking,
    ranking: ptBRRanking,
    'modules/campaign': ptBRCampaign,
    campaign: ptBRCampaign,
    'modules/reports': ptBRReports,
    reports: ptBRReports,
    dates: ptBRDates,
    profile: ptBRProfile,
    content: ptBRContent,
    integration: ptBRIntegration,
    'modules/integration': ptBRIntegration,
    'modules/quiz': ptBRQuiz,
    quiz: ptBRQuiz,
    'modules/product': ptBRProduct,
    product: ptBRProduct,
    'modules/function': ptBRFunction,
    function: ptBRFunction,
    'modules/cache': ptBRCache,
    cache: ptBRCache,
    'modules/social-posts': ptBRSocialPosts,
    'social-posts': ptBRSocialPosts,
    'modules/sac': ptBRSac,
    sac: ptBRSac,
  },
  'en-US': {
    common: enUSCommon,
    buttons: enUSButtons,
    errors: enUSErrors,
    validation: enUSValidation,
    'modules/navigation': enUSNavigation,
    'modules/person': enUSPerson,
    'modules/access': enUSAccess,
    'modules/budget': ptBRBudget, // Fallback to pt-BR
    'modules/finance': ptBRFinance, // Fallback to pt-BR
    'modules/settings': enUSSettings,
    settings: enUSSettings, // Alias para uso em useTranslation('settings')
    'modules/resource': enUSResource,
    'modules/import': enUSImport,
    resource: enUSResource,
    store: enUSStore,
    'modules/store': enUSStore,
    currency: enUSCurrency,
    'modules/currency': enUSCurrency,
    'modules/fragment': enUSFragment,
    dates: enUSDates,
    profile: enUSProfile,
    content: enUSContent,
    integration: enUSIntegration,
    'modules/integration': enUSIntegration,
    'modules/quiz': ptBRQuiz, // Fallback to pt-BR
    quiz: ptBRQuiz,
    'modules/product': enUSProduct,
    product: enUSProduct,
    'modules/function': enUSFunction,
    function: enUSFunction,
    'modules/social-posts': enUSSocialPosts,
    'social-posts': enUSSocialPosts,
    'modules/sac': enUSSac,
    sac: enUSSac,
  },
  // Alias en → en-US)
  'en': {
    common: enUSCommon,
    buttons: enUSButtons,
    errors: enUSErrors,
    validation: enUSValidation,
    'modules/navigation': enUSNavigation,
    'modules/person': enUSPerson,
    'modules/access': enUSAccess,
    'modules/budget': ptBRBudget,
    'modules/finance': ptBRFinance,
    'modules/settings': enUSSettings,
    settings: enUSSettings, // Alias para uso em useTranslation('settings')
    'modules/resource': enUSResource,
    'modules/import': enUSImport,
    resource: enUSResource,
    store: enUSStore,
    'modules/store': enUSStore,
    currency: enUSCurrency,
    'modules/currency': enUSCurrency,
    'modules/fragment': enUSFragment,
    integration: enUSIntegration,
    'modules/integration': enUSIntegration,
    'modules/quiz': ptBRQuiz, // Fallback to pt-BR
    quiz: ptBRQuiz,
    'modules/product': enUSProduct,
    product: enUSProduct,
    'modules/function': enUSFunction,
    function: enUSFunction,
    'modules/social-posts': enUSSocialPosts,
    'social-posts': enUSSocialPosts,
    'modules/sac': enUSSac,
    sac: enUSSac,
  },
  'es-ES': {
    common: esESCommon,
    buttons: esESButtons,
    errors: esESErrors,
    validation: esESValidation,
    'modules/navigation': esESNavigation,
    'modules/person': esESPerson,
    'modules/access': esESAccess,
    'modules/budget': ptBRBudget, // Fallback to pt-BR
    'modules/finance': ptBRFinance, // Fallback to pt-BR
    'modules/settings': esESSettings,
    settings: esESSettings, // Alias para uso em useTranslation('settings')
    'modules/resource': esESResource,
    'modules/import': esESImport,
    resource: esESResource,
    store: esESStore,
    'modules/store': esESStore,
    currency: esESCurrency,
    'modules/currency': esESCurrency,
    'modules/fragment': esESFragment,
    dates: esESDates,
    profile: esESProfile,
    content: esESContent,
    'modules/quiz': ptBRQuiz, // Fallback to pt-BR
    quiz: ptBRQuiz,
    'modules/product': esESProduct,
    product: esESProduct,
    'modules/function': esESFunction,
    function: esESFunction,
    'modules/social-posts': ptBRSocialPosts, // Fallback to pt-BR
    'social-posts': ptBRSocialPosts,
    'modules/sac': esESSac,
    sac: esESSac,
  },
  // Alias es → es-ES)
  'es': {
    common: esESCommon,
    buttons: esESButtons,
    errors: esESErrors,
    validation: esESValidation,
    'modules/navigation': esESNavigation,
    'modules/person': esESPerson,
    'modules/access': esESAccess,
    'modules/budget': ptBRBudget,
    'modules/finance': ptBRFinance,
    'modules/settings': esESSettings,
    settings: esESSettings, // Alias para uso em useTranslation('settings')
    'modules/resource': esESResource,
    'modules/import': esESImport,
    resource: esESResource,
    store: esESStore,
    'modules/store': esESStore,
    currency: esESCurrency,
    'modules/currency': esESCurrency,
    'modules/fragment': esESFragment,
    dates: esESDates,
    profile: esESProfile,
    content: esESContent,
    'modules/quiz': ptBRQuiz, // Fallback to pt-BR
    quiz: ptBRQuiz,
    'modules/product': esESProduct,
    product: esESProduct,
    'modules/function': esESFunction,
    function: esESFunction,
    'modules/social-posts': ptBRSocialPosts, // Fallback to pt-BR
    'social-posts': ptBRSocialPosts,
    'modules/sac': esESSac,
    sac: esESSac,
  },
  'fr-FR': {
    common: frFRCommon,
    buttons: frFRButtons,
    errors: frFRErrors,
    validation: frFRValidation,
    'modules/navigation': frFRNavigation,
    'modules/person': frFRPerson,
    'modules/access': ptBRAccess, // Fallback to pt-BR
    'modules/budget': ptBRBudget, // Fallback to pt-BR
    'modules/finance': ptBRFinance, // Fallback to pt-BR
    'modules/settings': frFRSettings,
    settings: frFRSettings, // Alias para uso em useTranslation('settings')
    'modules/resource': frFRResource,
    'modules/import': frFRImport,
    resource: frFRResource,
    store: ptBRStore, // Fallback to pt-BR
    'modules/store': ptBRStore, // Fallback to pt-BR
    currency: ptBRCurrency, // Fallback to pt-BR
    'modules/currency': ptBRCurrency, // Fallback to pt-BR
    'modules/fragment': frFRFragment,
    dates: frFRDates,
    profile: ptBRProfile, // Fallback to pt-BR
    content: ptBRContent, // Fallback to pt-BR
    'modules/quiz': ptBRQuiz, // Fallback to pt-BR
    quiz: ptBRQuiz,
    'modules/product': frFRProduct,
    product: frFRProduct,
    'modules/social-posts': ptBRSocialPosts, // Fallback to pt-BR
    'social-posts': ptBRSocialPosts,
    'modules/sac': ptBRSac, // Fallback to pt-BR
    sac: ptBRSac,
  },
  'ja-JP': {
    common: jaJPCommon,
    buttons: jaJPButtons,
    errors: jaJPErrors,
    validation: jaJPValidation,
    'modules/navigation': jaJPNavigation,
    'modules/person': jaJPPerson,
    'modules/access': ptBRAccess, // Fallback to pt-BR
    'modules/budget': ptBRBudget, // Fallback to pt-BR
    'modules/finance': ptBRFinance, // Fallback to pt-BR
    'modules/settings': jaJPSettings,
    settings: jaJPSettings, // Alias para uso em useTranslation('settings')
    'modules/resource': jaJPResource,
    'modules/import': ptBRImport, // Fallback to pt-BR
    resource: jaJPResource,
    store: ptBRStore, // Fallback to pt-BR
    'modules/store': ptBRStore, // Fallback to pt-BR
    currency: ptBRCurrency, // Fallback to pt-BR
    'modules/currency': ptBRCurrency, // Fallback to pt-BR
    'modules/fragment': jaJPFragment,
    dates: jaJPDates,
    profile: ptBRProfile, // Fallback to pt-BR
    content: ptBRContent, // Fallback to pt-BR
    'modules/quiz': ptBRQuiz, // Fallback to pt-BR
    quiz: ptBRQuiz,
    'modules/product': ptBRProduct, // Fallback to pt-BR
    product: ptBRProduct,
    'modules/function': ptBRFunction, // Fallback to pt-BR
    function: ptBRFunction,
    'modules/social-posts': ptBRSocialPosts, // Fallback to pt-BR
    'social-posts': ptBRSocialPosts,
    'modules/sac': ptBRSac, // Fallback to pt-BR
    sac: ptBRSac,
  },
  'zh-CN': {
    common: zhCNCommon,
    buttons: zhCNButtons,
    errors: zhCNErrors,
    validation: zhCNValidation,
    'modules/navigation': zhCNNavigation,
    'modules/person': zhCNPerson,
    'modules/access': ptBRAccess, // Fallback to pt-BR
    'modules/budget': ptBRBudget, // Fallback to pt-BR
    'modules/finance': ptBRFinance, // Fallback to pt-BR
    'modules/settings': zhCNSettings,
    settings: zhCNSettings, // Alias para uso em useTranslation('settings')
    'modules/resource': zhCNResource,
    'modules/import': ptBRImport, // Fallback to pt-BR
    resource: zhCNResource,
    store: ptBRStore, // Fallback to pt-BR
    'modules/store': ptBRStore, // Fallback to pt-BR
    currency: ptBRCurrency, // Fallback to pt-BR
    'modules/currency': ptBRCurrency, // Fallback to pt-BR
    'modules/fragment': zhCNFragment,
    dates: zhCNDates,
    profile: ptBRProfile, // Fallback to pt-BR
    content: ptBRContent, // Fallback to pt-BR
    'modules/quiz': ptBRQuiz, // Fallback to pt-BR
    quiz: ptBRQuiz,
    'modules/product': ptBRProduct, // Fallback to pt-BR
    product: ptBRProduct,
    'modules/function': ptBRFunction, // Fallback to pt-BR
    function: ptBRFunction,
    'modules/social-posts': ptBRSocialPosts, // Fallback to pt-BR
    'social-posts': ptBRSocialPosts,
    'modules/sac': ptBRSac, // Fallback to pt-BR
    sac: ptBRSac,
  },
  'ar-SA': {
    common: arSACommon,
    buttons: arSAButtons,
    errors: arSAErrors,
    validation: arSAValidation,
    'modules/navigation': arSANavigation,
    'modules/person': arSAPerson,
    'modules/access': ptBRAccess, // Fallback to pt-BR
    'modules/budget': ptBRBudget, // Fallback to pt-BR
    'modules/finance': ptBRFinance, // Fallback to pt-BR
    'modules/settings': arSASettings,
    settings: arSASettings, // Alias para uso em useTranslation('settings')
    'modules/resource': arSAResource,
    'modules/import': ptBRImport, // Fallback to pt-BR
    resource: arSAResource,
    store: ptBRStore, // Fallback to pt-BR
    'modules/store': ptBRStore, // Fallback to pt-BR
    currency: ptBRCurrency, // Fallback to pt-BR
    'modules/currency': ptBRCurrency, // Fallback to pt-BR
    'modules/fragment': arSAFragment,
    dates: arSADates,
    profile: ptBRProfile, // Fallback to pt-BR
    content: ptBRContent, // Fallback to pt-BR
    'modules/quiz': ptBRQuiz, // Fallback to pt-BR
    quiz: ptBRQuiz,
    'modules/product': ptBRProduct, // Fallback to pt-BR
    product: ptBRProduct,
    'modules/function': ptBRFunction, // Fallback to pt-BR
    function: ptBRFunction,
    'modules/social-posts': ptBRSocialPosts, // Fallback to pt-BR
    'social-posts': ptBRSocialPosts,
    'modules/sac': ptBRSac, // Fallback to pt-BR
    sac: ptBRSac,
  },
  'de-DE': {
    common: deDECommon,
    buttons: ptBRButtons, // Fallback to pt-BR
    errors: deDEErrors,
    validation: deDEValidation,
    'modules/navigation': ptBRNavigation, // Fallback to pt-BR
    'modules/person': ptBRPerson, // Fallback to pt-BR
    'modules/access': ptBRAccess, // Fallback to pt-BR
    'modules/budget': ptBRBudget, // Fallback to pt-BR
    'modules/finance': ptBRFinance, // Fallback to pt-BR
    'modules/settings': deDESettings,
    settings: deDESettings, // Alias para uso em useTranslation('settings')
    'modules/resource': deDEResource,
    'modules/import': ptBRImport, // Fallback to pt-BR
    resource: deDEResource,
    store: deDEStore,
    'modules/store': deDEStore,
    currency: ptBRCurrency, // Fallback to pt-BR
    'modules/currency': ptBRCurrency, // Fallback to pt-BR
    'modules/fragment': ptBRFragment, // Fallback to pt-BR
    dates: deDEDates,
    profile: ptBRProfile, // Fallback to pt-BR
    content: ptBRContent, // Fallback to pt-BR
    'modules/quiz': ptBRQuiz, // Fallback to pt-BR
    quiz: ptBRQuiz,
    'modules/product': ptBRProduct, // Fallback to pt-BR
    product: ptBRProduct,
    'modules/function': ptBRFunction, // Fallback to pt-BR
    function: ptBRFunction,
    'modules/social-posts': ptBRSocialPosts, // Fallback to pt-BR
    'social-posts': ptBRSocialPosts,
    'modules/sac': ptBRSac, // Fallback to pt-BR
    sac: ptBRSac,
  },
  // Alias fr → fr-FR
  'fr': {
    common: frFRCommon,
    buttons: frFRButtons,
    errors: frFRErrors,
    validation: frFRValidation,
    'modules/navigation': frFRNavigation,
    'modules/person': frFRPerson,
    'modules/access': ptBRAccess,
    'modules/budget': ptBRBudget,
    'modules/finance': ptBRFinance,
    'modules/settings': frFRSettings,
    settings: frFRSettings, // Alias para uso em useTranslation('settings')
    'modules/resource': frFRResource,
    'modules/import': frFRImport,
    resource: frFRResource,
    store: ptBRStore,
    'modules/store': ptBRStore,
    currency: ptBRCurrency,
    'modules/currency': ptBRCurrency,
    'modules/fragment': frFRFragment,
    dates: frFRDates,
    profile: ptBRProfile, // Fallback to pt-BR
    content: ptBRContent, // Fallback to pt-BR
    'modules/quiz': ptBRQuiz, // Fallback to pt-BR
    quiz: ptBRQuiz,
    'modules/product': frFRProduct,
    product: frFRProduct,
    'modules/function': frFRFunction,
    function: frFRFunction,
    'modules/social-posts': ptBRSocialPosts, // Fallback to pt-BR
    'social-posts': ptBRSocialPosts,
  },
  // Alias ja → ja-JP
  'ja': {
    common: jaJPCommon,
    buttons: jaJPButtons,
    errors: jaJPErrors,
    validation: jaJPValidation,
    'modules/navigation': jaJPNavigation,
    'modules/person': jaJPPerson,
    'modules/access': ptBRAccess,
    'modules/budget': ptBRBudget,
    'modules/finance': ptBRFinance,
    'modules/settings': jaJPSettings,
    settings: jaJPSettings, // Alias para uso em useTranslation('settings')
    'modules/resource': jaJPResource,
    'modules/import': ptBRImport, // Fallback to pt-BR
    resource: jaJPResource,
    store: ptBRStore,
    'modules/store': ptBRStore,
    currency: ptBRCurrency,
    'modules/currency': ptBRCurrency,
    'modules/fragment': jaJPFragment,
    dates: jaJPDates,
    profile: ptBRProfile, // Fallback to pt-BR
    content: ptBRContent, // Fallback to pt-BR
    'modules/quiz': ptBRQuiz, // Fallback to pt-BR
    quiz: ptBRQuiz,
    'modules/product': ptBRProduct, // Fallback to pt-BR
    product: ptBRProduct,
    'modules/function': ptBRFunction, // Fallback to pt-BR
    function: ptBRFunction,
    'modules/social-posts': ptBRSocialPosts, // Fallback to pt-BR
    'social-posts': ptBRSocialPosts,
  },
  // Alias zh → zh-CN
  'zh': {
    common: zhCNCommon,
    buttons: zhCNButtons,
    errors: zhCNErrors,
    validation: zhCNValidation,
    'modules/navigation': zhCNNavigation,
    'modules/person': zhCNPerson,
    'modules/access': ptBRAccess,
    'modules/budget': ptBRBudget,
    'modules/finance': ptBRFinance,
    'modules/settings': zhCNSettings,
    settings: zhCNSettings, // Alias para uso em useTranslation('settings')
    'modules/resource': zhCNResource,
    'modules/import': ptBRImport, // Fallback to pt-BR
    resource: zhCNResource,
    store: ptBRStore,
    'modules/store': ptBRStore,
    currency: ptBRCurrency,
    'modules/currency': ptBRCurrency,
    'modules/fragment': zhCNFragment,
    dates: zhCNDates,
    profile: ptBRProfile, // Fallback to pt-BR
    content: ptBRContent, // Fallback to pt-BR
    'modules/quiz': ptBRQuiz, // Fallback to pt-BR
    quiz: ptBRQuiz,
    'modules/product': ptBRProduct, // Fallback to pt-BR
    product: ptBRProduct,
    'modules/function': ptBRFunction, // Fallback to pt-BR
    function: ptBRFunction,
  },
  // Alias ar → ar-SA
  'ar': {
    common: arSACommon,
    buttons: arSAButtons,
    errors: arSAErrors,
    validation: arSAValidation,
    'modules/navigation': arSANavigation,
    'modules/person': arSAPerson,
    'modules/access': ptBRAccess,
    'modules/budget': ptBRBudget,
    'modules/finance': ptBRFinance,
    'modules/settings': arSASettings,
    settings: arSASettings, // Alias para uso em useTranslation('settings')
    'modules/resource': arSAResource,
    'modules/import': ptBRImport, // Fallback to pt-BR
    resource: arSAResource,
    store: ptBRStore,
    'modules/store': ptBRStore,
    currency: ptBRCurrency,
    'modules/currency': ptBRCurrency,
    'modules/fragment': arSAFragment,
    dates: arSADates,
    profile: ptBRProfile, // Fallback to pt-BR
    content: ptBRContent, // Fallback to pt-BR
    'modules/quiz': ptBRQuiz, // Fallback to pt-BR
    quiz: ptBRQuiz,
    'modules/product': ptBRProduct, // Fallback to pt-BR
    product: ptBRProduct,
    'modules/function': ptBRFunction, // Fallback to pt-BR
    function: ptBRFunction,
  },
  // Alias de → de-DE
  'de': {
    common: deDECommon,
    buttons: ptBRButtons,
    errors: deDEErrors,
    validation: deDEValidation,
    'modules/navigation': ptBRNavigation,
    'modules/person': ptBRPerson,
    'modules/access': ptBRAccess,
    'modules/budget': ptBRBudget,
    'modules/finance': ptBRFinance,
    'modules/settings': deDESettings,
    settings: deDESettings, // Alias para uso em useTranslation('settings')
    'modules/resource': deDEResource,
    'modules/import': ptBRImport, // Fallback to pt-BR
    resource: deDEResource,
    store: deDEStore,
    'modules/store': deDEStore,
    currency: ptBRCurrency,
    'modules/currency': ptBRCurrency,
    'modules/fragment': ptBRFragment,
    dates: deDEDates,
    profile: ptBRProfile, // Fallback to pt-BR
    content: ptBRContent, // Fallback to pt-BR
    'modules/quiz': ptBRQuiz, // Fallback to pt-BR
    quiz: ptBRQuiz,
    'modules/product': ptBRProduct, // Fallback to pt-BR
    product: ptBRProduct,
    'modules/function': ptBRFunction, // Fallback to pt-BR
    function: ptBRFunction,
    'modules/social-posts': ptBRSocialPosts, // Fallback to pt-BR
    'social-posts': ptBRSocialPosts,
  },
};

// ==========================================
// CONFIGURACAO DO I18NEXT
// ==========================================

i18n
  // Detectar idioma do navegador
  .use(LanguageDetector)
  // Integrar com React
  .use(initReactI18next)
  // Inicializar
  .init({
    // Recursos bundled (nao usa HTTP)
    resources,

    // Idioma padrao
    fallbackLng: 'pt-BR',

    // Idiomas suportados (incluindo aliases sem regiao)
    supportedLngs: [
      ...supportedLanguages.map(l => l.code),
      'pt', // Alias para pt-BR
      'en', // Alias para en-US
      'es', // Alias para es-ES
      'fr', // Alias para fr-FR
      'de', // Alias para de-DE
      'ja', // Alias para ja-JP
      'zh', // Alias para zh-CN
      'ar', // Alias para ar-SA
    ],

    // Mapeamento de idiomas sem regiao para com regiao
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,

    // Namespaces padrao
    ns: [...defaultNamespaces, ...moduleNamespaces],
    defaultNS: 'common',

    // Deteccao de idioma
    detection: {
      // Ordem de deteccao
      order: ['localStorage', 'navigator', 'htmlTag'],
      // Chave no localStorage
      lookupLocalStorage: 'tmf_language',
      // Cachear no localStorage
      caches: ['localStorage'],
    },

    // Interpolacao
    interpolation: {
      // React ja faz escape
      escapeValue: false,
      // Formato de placeholder: {param}
      prefix: '{',
      suffix: '}',
    },

    // React
    react: {
      // Desabilitar Suspense para evitar problemas de carregamento
      useSuspense: false,
    },

    // Debug (desabilitar em producao)
    debug: import.meta.env.DEV,
  });

export default i18n;
